import { useEffect, useState } from "react";
import { baseUrl, token } from "./api.ts";

/**
 * An employee's face as an object URL, for the someone-else picker
 * (`approve.tsx`, `#230`) — on the same terms as `apps/pos/src/lib/staff.ts`'s
 * photo cache: `<img src>` cannot carry the bearer `GET
 * /store/staff/{id}/photo` needs, so the bytes are fetched and held as an
 * object URL instead of the URL itself.
 *
 * Memory only, keyed by employee id and photo version: the picker is opened
 * for one approval and closed, not kept around for a shift, so there is
 * nothing here to evict.
 */
const photoCache = new Map<string, string>();
const photoLoads = new Map<string, Promise<string | null>>();

function loadPhoto(employeeId: string, photoVersion: string): Promise<string | null> {
  const key = `${employeeId}:${photoVersion}`;
  const cached = photoCache.get(key);
  if (cached) return Promise.resolve(cached);

  const inflight = photoLoads.get(key);
  if (inflight) return inflight;

  const promise = (async () => {
    const held = token();
    const response = await fetch(
      `${baseUrl()}/api/store/staff/${employeeId}/photo?v=${encodeURIComponent(photoVersion)}`,
      { headers: held ? { Authorization: `Bearer ${held}` } : {} },
    );
    if (!response.ok) return null;
    const url = URL.createObjectURL(await response.blob());
    photoCache.set(key, url);
    return url;
  })().finally(() => photoLoads.delete(key));

  photoLoads.set(key, promise);
  return promise;
}

/** Never blocks: renders `null` — the caller's initials fallback — until the fetch resolves. */
export function useEmployeePhoto(id: string, photoVersion: string | null): string | null {
  const key = photoVersion ? `${id}:${photoVersion}` : null;
  const [src, setSrc] = useState<string | null>(() => (key ? (photoCache.get(key) ?? null) : null));

  useEffect(() => {
    if (!photoVersion || src) return;
    let cancelled = false;
    void loadPhoto(id, photoVersion).then((url) => {
      if (!cancelled) setSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [id, photoVersion, src]);

  return src;
}
